<template>
  <!-- Collapsed: some pages have their own controls near the top (search bars, filters) that a
       full-width banner would sit over -- this shrinks it to a small corner badge instead.
       Collapsing only applies while creating; the completion summary always shows in full since
       it's brief and auto-hides on its own (see the `creating` watch below). -->
  <div
    v-if="visible && effectivelyCollapsed"
    class="fixed bottom-4 right-4 z-[200]"
  >
    <LlSecondaryButton
      :color="PROGRESS_COLOR"
      size="small"
      :loading="true"
      class="shadow-lg"
      aria-label="Batch invoice creation in progress -- click to expand"
      data-cy="bulk-invoice-create-badge"
      @click="collapsed = false"
    >
      {{ store.creatingProcessed }} / {{ store.creatingTotal }}
    </LlSecondaryButton>
  </div>

  <!-- z-[200], not Tailwind's z-50: the app's own fixed .header sits at z-index 100, so this
       banner needs to clear that to be visible on every route rather than rendering behind it.
       top-[73px] (the header's own height) sits it just below that header instead of
       overlapping the nav links. -->
  <div
    v-else-if="visible"
    class="fixed top-[73px] inset-x-0 z-[200] flex justify-center px-4 pt-2 pointer-events-none"
  >
    <AlertBar
      :type="alertType"
      :color="alertColor"
      class="shadow-md max-w-xl w-full pointer-events-auto"
      data-cy="bulk-invoice-create-banner"
    >
      <div class="flex items-center justify-between gap-3">
        <span>
          {{ bannerText }}
          <!-- aria-hidden: ticks every second while creating, so it must stay out of the
               role="alert" live region's announced content -- see elapsedLabel below. -->
          <span v-if="elapsedLabel" aria-hidden="true">({{ elapsedLabel }})</span>
        </span>
        <LlSecondaryButton
          v-if="store.creating"
          variant="text"
          color="info"
          size="x-small"
          class="shrink-0"
          aria-label="Collapse progress to a small badge"
          data-cy="bulk-invoice-create-collapse"
          @click="collapsed = true"
        >
          Collapse
        </LlSecondaryButton>
      </div>
    </AlertBar>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useBulkInvoiceCreateStore } from '@/stores/billing/useBulkInvoiceCreateStore';
import { getPinia } from '@/createPiniaSingleton';
import AlertBar from '@/components/AlertBar.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';

// Mounted once, globally (see bulkInvoiceCreateBanner.ts) -- a run survives navigating away
// from Ready to Bill, so this has to be visible regardless of which route is active.
const store = useBulkInvoiceCreateStore(getPinia());

// Matches tailwind.config.js's green.light / bg-green-light, the same green as the "Create
// Draft Invoices" button that starts this run -- Vuetify's own `type="info"` blue reads as an
// unrelated status color here, not "the thing you just clicked is still going".
const PROGRESS_COLOR = 'primary';

const SUMMARY_VISIBLE_MS = 8000;
const showSummary = ref(false);
let summaryTimeout: ReturnType<typeof setTimeout> | null = null;

// Collapsed is a per-viewer display preference, not run state -- it lives here, not in the
// store. Only meaningful while creating (see effectivelyCollapsed): the completion summary
// always shows in full regardless, since a collapsed badge could otherwise hide a failure the
// user never explicitly dismissed.
const collapsed = ref(false);

watch(
  () => store.creating,
  (creating, wasCreating) => {
    if (creating && !wasCreating) {
      collapsed.value = false;
    }
    if (!creating && wasCreating) {
      showSummary.value = true;
      if (summaryTimeout) clearTimeout(summaryTimeout);
      summaryTimeout = setTimeout(() => {
        showSummary.value = false;
      }, SUMMARY_VISIBLE_MS);
    }
  },
);

// Progress otherwise only advances at chunk boundaries, which at a realistic per-invoice rate
// can be minutes apart -- this ticks `now` every second so elapsedLabel below reads as a live
// clock, telling a long run apart from a stuck one in between those updates.
const now = ref(Date.now());
let tickInterval: ReturnType<typeof setInterval> | null = null;
watch(
  () => store.creating,
  (creating) => {
    if (creating) {
      now.value = Date.now();
      tickInterval = setInterval(() => {
        now.value = Date.now();
      }, 1000);
    } else if (tickInterval) {
      clearInterval(tickInterval);
      tickInterval = null;
    }
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  if (tickInterval) clearInterval(tickInterval);
});

const elapsedLabel = computed(() => {
  if (!store.creating || !store.runStartedAt) return '';
  const elapsedSeconds = Math.max(0, Math.floor((now.value - store.runStartedAt) / 1000));
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s elapsed` : `${seconds}s elapsed`;
});

const visible = computed(() => store.creating || showSummary.value);
const effectivelyCollapsed = computed(() => collapsed.value && store.creating);

const alertType = computed(() => {
  if (store.creating) return 'info';
  const summary = store.lastRunSummary;
  if (!summary) return 'info';
  if (summary.failedCount === 0) return 'success';
  if (summary.failedCount === summary.total) return 'error';
  return 'warning';
});

// Only overrides color while creating -- the completion summary's success/warning/error
// colors are already meaningful status colors and shouldn't be repainted green.
const alertColor = computed(() => (store.creating ? PROGRESS_COLOR : undefined));

// This is the role="alert" live region's announced text -- elapsedLabel is deliberately not
// part of it (see the aria-hidden span in the template): it changes every second, and before
// this ticking clock existed, a screen reader only heard an update at chunk boundaries (minutes
// apart). Folding elapsedLabel in here would re-announce the whole banner once a second for the
// run's entire duration.
const bannerText = computed(() => {
  if (store.creating) {
    const kind = store.draft ? 'draft' : 'QuickBooks';
    const chunkSuffix =
      store.creatingChunkTotal > 1 ? ` (batch ${store.creatingChunk} of ${store.creatingChunkTotal})` : '';
    return `Creating ${store.creatingTotal} ${kind} invoice(s)${chunkSuffix}. You can safely navigate away -- this will keep running.`;
  }
  const summary = store.lastRunSummary;
  if (!summary) return '';
  if (summary.failedCount === 0) return summary.successMessagePlural;
  if (summary.failedCount === summary.total) {
    return `Failed to create ${summary.failedCount} invoice(s). See Ready to Bill for details.`;
  }
  return `${summary.total - summary.failedCount} of ${summary.total} invoice(s) created; ${summary.failedCount} failed. See Ready to Bill for details.`;
});
</script>
